import "./ProjectAttrsModal.css";
import "components/UI/input/KsprAutocomplete.css";

import { useLayoutEffect, useState } from "react";
import Draggable from "react-draggable";
import { useDispatch, useSelector } from "react-redux";

import CloseIcon from "@mui/icons-material/Close";
import { IconButton, Tooltip } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";

import KsprButton from "components/UI/buttons/KsprButton";
import KsprInput from "components/UI/input/KsprInput";
import { getNorthTerrs, getNrspVers, getPriceZones, getProjectObjTypes, getProjectWorkTypes } from "slices/books/booksSelectors";
import { fetchNrspVers, fetchPriceZones } from "slices/books/services";
import { fetchProject, setProjectAttrsVisible } from "slices/project_work";
import { patchProject } from "slices/projects";

const getAutocompeleStyle = () => {
	return {
		width: "100%",
		"& legend": {
			fontSize: "9px",
		},
	};
};

const ProjectAttrsModal = () => {
	const dispatch = useDispatch();
	const projectAttrs = useSelector((state) => state.projectWork.main);
	const [data, setData] = useState({});

	const priceZone = useSelector(getPriceZones);
	const nrspVers = useSelector(getNrspVers);
	const northTerrs = useSelector(getNorthTerrs);
	const projectObjTypes = useSelector(getProjectObjTypes);
	const projectWorkTypes = useSelector(getProjectWorkTypes);

	const [selectedPriceZone, setSelectedPriceZone] = useState({});
	const [selectedNorthTerr, setSelectedNorthTerr] = useState({});
	const [selectedNrspVers, setSelectedNrspVers] = useState({});
	const [selectedProjectObjType, setSelectedProjectObjType] = useState({});
	const [selectedProjectWorkType, setSelectedProjectWorkType] = useState({});

	useLayoutEffect(() => {
		dispatch(fetchPriceZones()).then((data) => {
			setSelectedPriceZone(data.payload.find((x) => x.id === projectAttrs.price_zone_id));
		});
		dispatch(fetchNrspVers()).then((data) => {
			setSelectedNrspVers(data.payload.find((x) => x.id === projectAttrs.nrsp_vers_id));
		});
		setSelectedNorthTerr(northTerrs.find((x) => x.id === projectAttrs.north_reg_terr_id));
		setSelectedProjectObjType(projectObjTypes.find((x) => x.id === projectAttrs.object_type_id));
		setSelectedProjectWorkType(projectWorkTypes.find((x) => x.id === projectAttrs.work_type_id));

		setData(projectAttrs);
	}, [projectAttrs, dispatch, northTerrs, projectObjTypes, projectWorkTypes]);

	const handleChangeData = ({ key, value }) => setData((prev) => ({ ...prev, [key]: value }));

	const genRequestBody = () => {
		return {
			id: data.id,
			code: data.code,
			name: data.name,
			official_construction_name: data.official_construction_name,
			official_object_name: data.official_object_name,
			price_zone_id: selectedPriceZone?.id,
			north_reg_terr_id: selectedNorthTerr?.id,
			nrsp_vers_id: selectedNrspVers?.id,
			object_type_id: selectedProjectObjType?.id,
			work_type_id: selectedProjectWorkType?.id,
		};
	};

	return (
		<Draggable handle=".ProjectAttrsModalTitle">
			<Dialog
				className="ProjectAttrsModal"
				sx={{ "& .MuiDialog-paper": { width: "50vw", maxWidth: "50vw", height: "510px" } }}
				open
				slotProps={{ backdrop: { style: { backgroundColor: "transparent" } } }}
				onKeyDown={(e) => {
					if (e?.key === "Escape") dispatch(setProjectAttrsVisible(false));
				}}
			>
				<DialogTitle className="ProjectAttrsModalTitle">
					<div>Свойства проекта</div>
					<Tooltip title="Закрыть">
						<IconButton aria-label="Закрыть" onClick={() => dispatch(setProjectAttrsVisible(false))}>
							<CloseIcon />
						</IconButton>
					</Tooltip>
				</DialogTitle>
				<DialogContent className="ProjectAttrsModalContent">
					<Grid container mb={2} spacing={2} justifyContent="center" alignItems="center">
						<Grid item xs={6}>
							<KsprInput label="Дата создания" size="small" value={data.date_create || ""} disabled={true} />
						</Grid>
						<Grid item xs={6}>
							<KsprInput label="Пользователь" size="small" value={data.username || ""} disabled={true} />
						</Grid>
					</Grid>
					<Grid container mb={2} spacing={2} justifyContent="center" alignItems="center">
						<Grid item xs={12}>
							<KsprInput
								label="Код проекта"
								size="small"
								value={data.code || ""}
								onChange={(e) => handleChangeData({ key: "code", value: e.target.value })}
							/>
						</Grid>
					</Grid>
					<Grid container mb={2} spacing={2} justifyContent="center" alignItems="center">
						<Grid item xs={12}>
							<KsprInput
								label="Наименование проекта"
								size="small"
								value={data.name || ""}
								onChange={(e) => handleChangeData({ key: "name", value: e.target.value })}
							/>
						</Grid>
					</Grid>
					<Grid container mb={2} spacing={2} justifyContent="center" alignItems="center">
						<Grid item xs={6}>
							<div className="KsprAutocomplete">
								<Autocomplete
									sx={getAutocompeleStyle()}
									options={priceZone}
									value={selectedPriceZone}
									getOptionLabel={(option) => option.name || ""}
									size="small"
									noOptionsText="нет данных"
									onInputChange={(e, val) => {
										const inpVal = priceZone.find((x) => x["name"] === val);
										if (inpVal) setSelectedPriceZone(inpVal);
									}}
									renderInput={(params) => <TextField {...params} InputLabelProps={{ shrink: true }} label="Ценовая зона" />}
								/>
							</div>
						</Grid>
						<Grid item xs={6}>
							<div className="KsprAutocomplete">
								<Autocomplete
									sx={getAutocompeleStyle()}
									options={northTerrs}
									value={selectedNorthTerr}
									getOptionLabel={(option) => option.name || ""}
									size="small"
									noOptionsText="нет данных"
									onInputChange={(e, val) => {
										const inpVal = northTerrs.find((x) => x["name"] === val);
										if (inpVal) setSelectedNorthTerr(inpVal);
									}}
									renderInput={(params) => <TextField {...params} InputLabelProps={{ shrink: true }} label="Признак территории" />}
								/>
							</div>
						</Grid>
					</Grid>
					<Grid container mb={2} spacing={2} justifyContent="center" alignItems="center">
						<Grid item xs={6}>
							<div className="KsprAutocomplete">
								<Autocomplete
									sx={getAutocompeleStyle()}
									options={projectWorkTypes}
									value={selectedProjectWorkType}
									getOptionLabel={(option) => option.name || ""}
									size="small"
									noOptionsText="нет данных"
									onInputChange={(e, val) => {
										const inpVal = projectWorkTypes.find((x) => x["name"] === val);
										if (inpVal) setSelectedProjectWorkType(inpVal);
									}}
									renderInput={(params) => <TextField {...params} InputLabelProps={{ shrink: true }} label="Вид работ" />}
								/>
							</div>
						</Grid>
						<Grid item xs={6}>
							<div className="KsprAutocomplete">
								<Autocomplete
									sx={getAutocompeleStyle()}
									options={projectObjTypes}
									value={selectedProjectObjType}
									getOptionLabel={(option) => option.name || ""}
									size="small"
									noOptionsText="нет данных"
									onInputChange={(e, val) => {
										const inpVal = projectObjTypes.find((x) => x["name"] === val);
										if (inpVal) setSelectedProjectObjType(inpVal);
									}}
									renderInput={(params) => <TextField {...params} InputLabelProps={{ shrink: true }} label="Тип объекта" />}
								/>
							</div>
						</Grid>
					</Grid>
					<Grid container mb={2} spacing={2} justifyContent="center" alignItems="center">
						<Grid item xs={12}>
							<div className="KsprAutocomplete">
								<Autocomplete
									sx={getAutocompeleStyle()}
									options={nrspVers}
									value={selectedNrspVers}
									getOptionLabel={(option) => option.name || ""}
									size="small"
									noOptionsText="нет данных"
									onInputChange={(e, val) => {
										const inpVal = nrspVers.find((x) => x["name"] === val);
										if (inpVal) setSelectedNrspVers(inpVal);
									}}
									renderInput={(params) => <TextField {...params} InputLabelProps={{ shrink: true }} label="Номер приказа НРСП" />}
								/>
							</div>
						</Grid>
					</Grid>
					<Grid container mb={2} spacing={2} justifyContent="center" alignItems="center">
						<Grid item xs={12}>
							<KsprInput
								label="Полное наименование стройки"
								size="small"
								value={data.official_construction_name || ""}
								onChange={(e) => handleChangeData({ key: "official_construction_name", value: e.target.value })}
							/>
						</Grid>
					</Grid>
					<Grid container mb={2} spacing={2} justifyContent="center" alignItems="center">
						<Grid item xs={12}>
							<KsprInput
								label="Полное наименование объекта"
								size="small"
								value={data.official_object_name || ""}
								onChange={(e) => handleChangeData({ key: "official_object_name", value: e.target.value })}
							/>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions>
					<div className="btnWrapper">
						<KsprButton
							sx={{ ml: "auto" }}
							variant="text"
							label={"Сохранить"}
							onClick={() => {
								dispatch(patchProject(genRequestBody())).then((x) => {
									dispatch(fetchProject(projectAttrs.id));
									dispatch(setProjectAttrsVisible(false));
								});
							}}
						/>
					</div>
				</DialogActions>
			</Dialog>
		</Draggable>
	);
};

export default ProjectAttrsModal;
